import { ChangeEvent, ReactNode, Ref, useCallback, useState } from 'react';
import {
  FormControl,
  FormControlProps,
  FormErrorMessage,
  FormHelperText,
  forwardRef,
  Input as ChakraInput,
  InputLeftElement,
  InputProps as ChakraInputProps,
  InputRightAddon,
  InputLeftAddon,
  InputRightElement,
  SystemStyleObject,
  IconProps,
  useMultiStyleConfig,
  InputGroup,
  BoxProps,
} from '@chakra-ui/react';
import Icon, { TypeIconName } from '../../Icon/Icon';
import { TooltipProps } from '../../Tooltip/Tooltip';
import FormLabel, { FormLabelProps } from '../FormLabel';
import { getDataAttributes, rem } from '../../../utils/utils';

type UsedFormControlProps = Omit<FormControlProps, 'label' | 'onBlur' | 'onChange'>;
type UsedChakraInputProps = Pick<
  ChakraInputProps,
  | 'onBlur'
  | 'onChange'
  | 'role'
  | 'name'
  | 'type'
  | 'value'
  | 'autoComplete'
  | 'autoFocus'
  | 'max'
  | 'maxLength'
  | 'min'
  | 'minLength'
  | 'pattern'
  | 'placeholder'
  | 'step'
>;

export interface InputProps extends UsedFormControlProps, UsedChakraInputProps {
  badge?: FormLabelProps['badge'];
  errorText?: ReactNode;
  infoTooltipLabel?: string;
  infoTooltipProps?: TooltipProps;
  isLoading?: boolean;
  isWarning?: boolean;
  helperText?: ReactNode;
  inputRef?: Ref<HTMLInputElement>;
  inputStyle?: SystemStyleObject;
  inputWrapperStyle?: BoxProps;
  label?: string;
  leftAddon?: ReactNode;
  leftAddonPlacement?: 'inside' | 'outside';
  leftIconColor?: IconProps['color'];
  leftIconName?: TypeIconName;
  recommendedLength?: number;
  rightAddon?: ReactNode;
  rightAddonPlacement?: 'inside' | 'outside';
  rightIconName?: TypeIconName;
  rightIconColor?: IconProps['color'];
  size?: 'lg' | 'md';
  warningText?: ReactNode;
}

/**
 * Input component is a component that is used to get user input in a text field.
 */
const Input = forwardRef<InputProps, 'div'>((props, ref) => {
  const {
    autoComplete,
    autoFocus,
    badge,
    defaultValue,
    errorText: errorTextProp,
    helperText,
    infoTooltipLabel,
    infoTooltipProps,
    inputRef,
    inputStyle,
    inputWrapperStyle,
    isDisabled,
    isInvalid,
    isLoading,
    isWarning,
    label,
    leftAddon,
    leftAddonPlacement,
    leftIconColor,
    leftIconName,
    max,
    maxLength,
    min,
    minLength,
    name,
    onBlur,
    onChange,
    pattern,
    placeholder,
    recommendedLength,
    rightAddon,
    rightAddonPlacement,
    rightIconName,
    rightIconColor,
    role,
    size = 'lg',
    step,
    type,
    value,
    warningText,
    ...rest
  } = props;

  const [valueLength, setValueLength] = useState(value?.toString().length || 0);

  const exceedsCharacterLimitError =
    recommendedLength && valueLength > recommendedLength ? 'Text exceeds the character limit.' : undefined;
  const errorText = errorTextProp || exceedsCharacterLimitError;

  const formControlProps = {
    isDisabled: isDisabled || isLoading,
    isInvalid: isInvalid || !!errorText,
    ...rest,
    ref,
  };
  const inputProps = {
    autoComplete,
    autoFocus,
    defaultValue,
    max,
    maxLength,
    min,
    minLength,
    name,
    onBlur,
    pattern,
    placeholder,
    ref: inputRef,
    role,
    step,
    size,
    type,
    value,
  };

  const dataAttributes = getDataAttributes(rest);

  const inputInlinePadding = size === 'lg' ? '15' : '11';

  const RightContentWrapper = rightAddonPlacement === 'inside' ? InputRightElement : InputRightAddon;
  const inputRightPadding =
    rightIconName || (rightAddon && rightAddonPlacement === 'inside') ? '43px' : inputInlinePadding;

  const LeftContentWrapper = leftAddonPlacement === 'inside' ? InputLeftElement : InputLeftAddon;
  const inputLeftPadding = leftIconName || (leftAddon && leftAddonPlacement === 'inside') ? '43px' : inputInlinePadding;

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValueLength(e.currentTarget.value.length);

    if (onChange) {
      onChange(e);
    }
  };

  const iconSize = size === 'lg' ? '24' : '16';
  const style = useMultiStyleConfig('Input');

  const [leftIconPos, setLeftIconPos] = useState(rem(12));

  const leftAddonRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setLeftIconPos(rem(node.getBoundingClientRect().width + 12));
    }
  }, []);

  const [rightIconPos, setRightIconPos] = useState(rem(12));

  const rightAddonRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setRightIconPos(rem(node.getBoundingClientRect().width + 12));
    }
  }, []);

  return (
    <FormControl {...formControlProps}>
      <FormLabel
        badge={badge}
        infoTooltipLabel={infoTooltipLabel}
        infoTooltipProps={infoTooltipProps}
        maxLength={maxLength || recommendedLength}
        valueLength={valueLength}
        withCounter={Boolean(maxLength || recommendedLength)}
      >
        {label}
      </FormLabel>
      <InputGroup sx={style.inputContainer} {...inputWrapperStyle}>
        {leftAddon && (
          <LeftContentWrapper ref={leftAddonRef} left="0">
            {leftAddon}
          </LeftContentWrapper>
        )}
        {leftIconName && (
          <InputLeftElement sx={style.leftIconWrapper} left={leftIconPos}>
            <Icon
              color={leftIconColor}
              name={leftIconName}
              size={iconSize}
              data-disabled={isDisabled || undefined}
              sx={style.icon}
            />
          </InputLeftElement>
        )}
        <ChakraInput
          paddingLeft={inputLeftPadding}
          paddingRight={inputRightPadding}
          {...inputProps}
          {...dataAttributes}
          onChange={onInputChange}
        />
        {rightAddon && (
          <RightContentWrapper ref={rightAddonRef} bottom="0">
            {rightAddon}
          </RightContentWrapper>
        )}
        {(rightIconName || isWarning || warningText || isInvalid) && (
          <InputRightElement sx={style.rightIconWrapper} right={rightIconPos}>
            {rightIconName && !isWarning && !warningText && !isInvalid && (
              <Icon
                color={rightIconColor}
                name={rightIconName}
                size={iconSize}
                sx={style.icon}
                data-disabled={isDisabled || undefined}
              />
            )}
            {!isInvalid && (isWarning || warningText) && (
              <Icon aria-hidden="true" name="WarningYellow" size={iconSize} sx={style.stateIcon} />
            )}
            {isInvalid && (
              <Icon
                aria-hidden="true"
                color="icon.negative"
                name="ErrorCircleFilled"
                size={iconSize}
                sx={style.stateIcon}
              />
            )}
          </InputRightElement>
        )}
      </InputGroup>
      {errorText && <FormErrorMessage as="p">{errorText}</FormErrorMessage>}
      {!errorText && warningText && (
        <FormHelperText color="text.body" as="p">
          {warningText}
        </FormHelperText>
      )}
      {!errorText && !warningText && helperText && (
        <FormHelperText as="p" data-disabled={isDisabled || undefined}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
});

export default Input;
